import React from 'react';
import { MediaKitSocials } from '../services/mediaKitService';
import {
    formatAtHandleDisplay,
    getFacebookUrl,
    getHudlUrl,
    getInstagramUrl,
    getLinkedInUrl,
    getTikTokUrl,
    getXUrl,
} from '../utils/socialMediaUtils';
import { MEDIA_KIT_SOCIAL_FIELDS } from '../utils/mediaKitFormUtils';
import { MEDIA_KIT_SOCIAL_PLATFORM_META } from './mediaKitSocialPlatforms';

interface MediaKitSocialOverviewProps {
    socials: MediaKitSocials;
}

type SocialPlatform = {
    key: keyof MediaKitSocials;
    label: string;
    iconClass: string;
    Icon: React.ComponentType<{ className?: string }>;
    getUrl: (value: string) => string | null;
    formatDisplay: (value: string) => string;
};

function formatHandle(value: string): string {
    return formatAtHandleDisplay(value);
}

function formatLinkedInDisplay(value: string): string {
    const slug = value
        .replace(/^https?:\/\/(www\.)?linkedin\.com\/(in|company)\//i, '')
        .replace(/\/$/, '');
    const username = slug.split('/').filter(Boolean)[0];
    return username ? `@${username}` : '@profile';
}

function formatHudlDisplay(value: string): string {
    const slug = value
        .replace(/^https?:\/\/(www\.)?hudl\.com\/(profile\/)?/i, '')
        .replace(/\/$/, '');
    const username = slug.split('/').filter(Boolean)[0];
    return username ? `@${username}` : '@profile';
}

const PLATFORM_URL_AND_FORMAT: Record<
    keyof MediaKitSocials,
    { getUrl: (value: string) => string | null; formatDisplay: (value: string) => string }
> = {
    instagramHandle: { getUrl: getInstagramUrl, formatDisplay: formatHandle },
    linkedinUrl: { getUrl: getLinkedInUrl, formatDisplay: formatLinkedInDisplay },
    hudlUrl: { getUrl: getHudlUrl, formatDisplay: formatHudlDisplay },
    tiktokHandle: { getUrl: getTikTokUrl, formatDisplay: formatHandle },
    facebookName: { getUrl: getFacebookUrl, formatDisplay: formatHandle },
    xHandle: { getUrl: getXUrl, formatDisplay: formatHandle },
};

/** Connect slot order matches the edit form (Instagram = 1 … X = 6). Empty slots are skipped. */
const PLATFORMS: SocialPlatform[] = MEDIA_KIT_SOCIAL_FIELDS.map(({ key, label }) => {
    const { iconClass, Icon } = MEDIA_KIT_SOCIAL_PLATFORM_META[key];
    const { getUrl, formatDisplay } = PLATFORM_URL_AND_FORMAT[key];
    return { key, label, iconClass, Icon, getUrl, formatDisplay };
});

export default function MediaKitSocialOverview({ socials }: MediaKitSocialOverviewProps) {
    const activePlatforms = PLATFORMS.filter(({ key }) => {
        const value = socials[key];
        return typeof value === 'string' && value.trim().length > 0;
    });

    if (activePlatforms.length === 0) {
        return <p className="media-kit-empty-copy">No social links added yet.</p>;
    }

    return (
        <div className="media-kit-social-overview-shell">
            <div
                className="media-kit-social-overview"
                data-social-count={activePlatforms.length}
            >
                {activePlatforms.map(({ key, iconClass, Icon, getUrl, formatDisplay }, slotIndex) => {
                    const value = socials[key] as string;
                    const href = getUrl(value);
                    return (
                        <div
                            key={key}
                            className="media-kit-social-overview-item"
                            data-slot-index={slotIndex + 1}
                        >
                            <span className={`media-kit-social-icon ${iconClass}`} aria-hidden="true">
                                <Icon className="media-kit-social-icon-svg" />
                            </span>
                            {href ? (
                                <a href={href} target="_blank" rel="noreferrer" className="media-kit-social-handle">
                                    {formatDisplay(value)}
                                </a>
                            ) : (
                                <span className="media-kit-social-handle">{formatDisplay(value)}</span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
